import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import productAnalyticsService from '../../services/api/product-analytics.service';
import { journeysService, ScrollDepthData } from '../../services/api/journeys.service';

const FLOW_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

const SCROLL_SEGMENTS = [
  { key: '0-25' as const, label: '0–25%', color: '#ef4444', darkColor: '#dc2626', desc: 'Barely scrolled' },
  { key: '25-50' as const, label: '25–50%', color: '#f59e0b', darkColor: '#d97706', desc: 'Partial scroll' },
  { key: '50-75' as const, label: '50–75%', color: '#06b6d4', darkColor: '#0891b2', desc: 'Good engagement' },
  { key: '75-100' as const, label: '75–100%', color: '#10b981', darkColor: '#059669', desc: 'Full scroll' },
];

// ─── Mini Sankey Flow Diagram ───────────────────────────────────────────────
interface FlowData { fromPage: string; toPage: string; transitions: number }

function PageFlowDiagram({ flows }: { flows: FlowData[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (flows.length === 0) return null;

    // Aggregate totals per source / target
    const sourceMap = new Map<string, number>();
    const targetMap = new Map<string, number>();
    for (const f of flows) {
      sourceMap.set(f.fromPage, (sourceMap.get(f.fromPage) || 0) + f.transitions);
      targetMap.set(f.toPage, (targetMap.get(f.toPage) || 0) + f.transitions);
    }

    // Sort by total traffic descending
    const sources = [...sourceMap.entries()].sort((a, b) => b[1] - a[1]);
    const targets = [...targetMap.entries()].sort((a, b) => b[1] - a[1]);

    // Dimensions — responsive label padding
    const width = containerWidth;
    const nodeWidth = 14;
    const nodePad = 6;
    const labelPadding = width < 500 ? 90 : width < 700 ? 130 : 180;
    const leftX = labelPadding;
    const rightX = Math.max(leftX + nodeWidth + 60, width - labelPadding - nodeWidth);

    // Compute total height needed (proportional to traffic)
    const totalSourceTraffic = sources.reduce((s, [, v]) => s + v, 0);
    const totalTargetTraffic = targets.reduce((s, [, v]) => s + v, 0);

    const minNodeH = 8;
    const availableH = Math.max(sources.length, targets.length) * 44;
    const height = Math.max(availableH, 200);

    // Compute node positions — proportional height
    const sourceUsableH = height - (sources.length - 1) * nodePad;
    const targetUsableH = height - (targets.length - 1) * nodePad;

    const sourceNodes: { name: string; y: number; h: number; total: number; color: string }[] = [];
    let sy = 0;
    sources.forEach(([name, total], i) => {
      const h = Math.max((total / totalSourceTraffic) * sourceUsableH, minNodeH);
      sourceNodes.push({ name, y: sy, h, total, color: FLOW_COLORS[i % FLOW_COLORS.length] });
      sy += h + nodePad;
    });

    const targetNodes: { name: string; y: number; h: number; total: number; color: string }[] = [];
    let ty = 0;
    targets.forEach(([name, total], i) => {
      const h = Math.max((total / totalTargetTraffic) * targetUsableH, minNodeH);
      targetNodes.push({ name, y: ty, h, total, color: FLOW_COLORS[(i + 3) % FLOW_COLORS.length] });
      ty += h + nodePad;
    });

    // Center both columns vertically
    const maxH = Math.max(sy - nodePad, ty - nodePad, 1);
    const topPad = 10;
    const totalH = maxH + topPad * 2;
    const sOffset = topPad + (maxH - (sy - nodePad)) / 2;
    const tOffset = topPad + (maxH - (ty - nodePad)) / 2;
    sourceNodes.forEach(n => (n.y += sOffset));
    targetNodes.forEach(n => (n.y += tOffset));

    // Compute link paths — track slot positions within each node
    const sSlots = new Map<string, number>();
    const tSlots = new Map<string, number>();
    sourceNodes.forEach(n => sSlots.set(n.name, n.y));
    targetNodes.forEach(n => tSlots.set(n.name, n.y));

    const links = flows.map((f, idx) => {
      const sNode = sourceNodes.find(n => n.name === f.fromPage)!;
      const tNode = targetNodes.find(n => n.name === f.toPage)!;
      const linkH = Math.max((f.transitions / sNode.total) * sNode.h, 2);
      const linkHt = Math.max((f.transitions / tNode.total) * tNode.h, 2);

      const y0 = sSlots.get(f.fromPage)!;
      const y1 = tSlots.get(f.toPage)!;
      sSlots.set(f.fromPage, y0 + linkH);
      tSlots.set(f.toPage, y1 + linkHt);

      const x0 = leftX + nodeWidth;
      const x1 = rightX;
      const midX = (x0 + x1) / 2;

      const path = `M${x0},${y0} C${midX},${y0} ${midX},${y1} ${x1},${y1} L${x1},${y1 + linkHt} C${midX},${y1 + linkHt} ${midX},${y0 + linkH} ${x0},${y0 + linkH} Z`;

      return { path, color: sNode.color, transitions: f.transitions, from: f.fromPage, to: f.toPage, idx };
    });

    return { sourceNodes, targetNodes, links, leftX, rightX, nodeWidth, width, height: totalH };
  }, [flows, containerWidth]);

  if (!layout || flows.length === 0) return null;

  const isHighlighted = (from: string, to: string) => {
    if (hoveredNode) return from === hoveredNode || to === hoveredNode;
    return hoveredLink === null;
  };

  const isNodeHighlighted = (name: string) => {
    if (hoveredNode) return name === hoveredNode;
    if (hoveredLink !== null) {
      const link = layout.links[hoveredLink];
      return link && (link.from === name || link.to === name);
    }
    return true;
  };

  const shortenPath = (path: string) => {
    const maxLen = containerWidth < 500 ? 14 : containerWidth < 700 ? 20 : 28;
    if (path.length <= maxLen) return path;
    return '...' + path.slice(-(maxLen - 3));
  };

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg width={Math.max(layout.width, 320)} height={layout.height} className="select-none">
        {/* Links (bezier paths) */}
        <g>
          {layout.links.map((link, i) => {
            const highlighted = isHighlighted(link.from, link.to);
            const isDirectHover = hoveredLink === i;
            return (
              <g key={i}>
                <path
                  d={link.path}
                  fill={link.color}
                  opacity={highlighted ? (isDirectHover ? 0.55 : 0.3) : 0.06}
                  className="transition-opacity duration-200"
                  onMouseEnter={() => setHoveredLink(i)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{ cursor: 'pointer' }}
                />
                {/* Hover tooltip */}
                {isDirectHover && (() => {
                  // Find midpoint of the path for tooltip
                  const sNode = layout.sourceNodes.find(n => n.name === link.from)!;
                  const tNode = layout.targetNodes.find(n => n.name === link.to)!;
                  const midX = (layout.leftX + layout.nodeWidth + layout.rightX) / 2;
                  const midY = (sNode.y + sNode.h / 2 + tNode.y + tNode.h / 2) / 2;
                  return (
                    <g>
                      <rect
                        x={midX - 40} y={midY - 14}
                        width={80} height={28} rx={6}
                        fill="rgba(17,24,39,0.92)" stroke="none"
                      />
                      <text x={midX} y={midY + 4} textAnchor="middle" fill="#fff" fontSize={13} fontWeight={700}>
                        {link.transitions}
                      </text>
                    </g>
                  );
                })()}
              </g>
            );
          })}
        </g>
        {/* Source nodes (left) */}
        {layout.sourceNodes.map(node => {
          const highlighted = isNodeHighlighted(node.name);
          return (
            <g key={`s-${node.name}`}
               onMouseEnter={() => setHoveredNode(node.name)}
               onMouseLeave={() => setHoveredNode(null)}
               style={{ cursor: 'pointer' }}
            >
              <rect
                x={layout.leftX} y={node.y}
                width={layout.nodeWidth} height={node.h}
                rx={4} fill={node.color}
                opacity={highlighted ? 1 : 0.2}
                className="transition-opacity duration-200"
              />
              <text
                x={layout.leftX - 8}
                y={node.y + node.h / 2 + 4}
                textAnchor="end"
                className="fill-gray-700 dark:fill-gray-300 transition-opacity duration-200"
                fontSize={containerWidth < 500 ? 10 : 12}
                fontFamily="monospace"
                opacity={highlighted ? 1 : 0.3}
              >
                {shortenPath(node.name)}
              </text>
            </g>
          );
        })}
        {/* Target nodes (right) */}
        {layout.targetNodes.map(node => {
          const highlighted = isNodeHighlighted(node.name);
          return (
            <g key={`t-${node.name}`}
               onMouseEnter={() => setHoveredNode(node.name)}
               onMouseLeave={() => setHoveredNode(null)}
               style={{ cursor: 'pointer' }}
            >
              <rect
                x={layout.rightX} y={node.y}
                width={layout.nodeWidth} height={node.h}
                rx={4} fill={node.color}
                opacity={highlighted ? 1 : 0.2}
                className="transition-opacity duration-200"
              />
              <text
                x={layout.rightX + layout.nodeWidth + 8}
                y={node.y + node.h / 2 + 4}
                textAnchor="start"
                className="fill-gray-700 dark:fill-gray-300 transition-opacity duration-200"
                fontSize={containerWidth < 500 ? 10 : 12}
                fontFamily="monospace"
                opacity={highlighted ? 1 : 0.3}
              >
                {shortenPath(node.name)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function PagesPage() {
  const { t } = useTranslation('analytics');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 86400000, endTime: Date.now(), rangeType: 'preset', presetValue: '7d',
  });
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState<Array<{ path: string; views: number; uniqueSessions: number; avgDuration: number; avgScrollDepth: number; avgLcp: number }>>([]);
  const [entryExit, setEntryExit] = useState<{ entryPages: Array<{ path: string; entries: number }>; exitPages: Array<{ path: string; exits: number }>; avgPagesPerSession: number } | null>(null);
  const [pageFlow, setPageFlow] = useState<FlowData[]>([]);
  const [scrollDepth, setScrollDepth] = useState<ScrollDepthData | null>(null);
  const [scrollByPage, setScrollByPage] = useState<Map<string, number>>(new Map());

  const appId = selectedAppId || 'all';
  const startISO = new Date(timeRange.startTime).toISOString();
  const endISO = new Date(timeRange.endTime).toISOString();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pagesData, entryExitData, flowData, scrollData, scrollPageData] = await Promise.allSettled([
        productAnalyticsService.getTopPages(appId, startISO, endISO),
        productAnalyticsService.getEntryExitPages(appId, startISO, endISO),
        productAnalyticsService.getPageFlow(appId, startISO, endISO),
        journeysService.getScrollDepth(appId, undefined, new Date(timeRange.startTime), new Date(timeRange.endTime)),
        productAnalyticsService.getScrollDepthByPage(appId, startISO, endISO),
      ]);
      if (pagesData.status === 'fulfilled') setPages(pagesData.value || []);
      if (entryExitData.status === 'fulfilled') setEntryExit(entryExitData.value);
      if (flowData.status === 'fulfilled') setPageFlow(flowData.value || []);
      if (scrollData.status === 'fulfilled') setScrollDepth(scrollData.value || null);
      if (scrollPageData.status === 'fulfilled') {
        const map = new Map<string, number>();
        (scrollPageData.value || []).forEach((item: { path: string; avgDepth: number }) => {
          // Normalize path: extract pathname from full URL
          let key = item.path;
          try { key = new URL(item.path).pathname; } catch { /* already a path */ }
          map.set(key, item.avgDepth);
        });
        setScrollByPage(map);
      }
    } catch (err) {
      console.error('Failed to load pages data:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, startISO, endISO]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalViews = pages.reduce((s, p) => s + p.views, 0);
  const uniquePagesCount = pages.length;
  const avgPPS = entryExit?.avgPagesPerSession || 0;

  const hasLcpData = pages.some(p => p.avgLcp > 0);
  const avgLcp = hasLcpData ? pages.filter(p => p.avgLcp > 0).reduce((s, p) => s + p.avgLcp, 0) / pages.filter(p => p.avgLcp > 0).length : 0;

  const fmt = (n: number) => { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); };
  const fmtMs = (ms: number) => { if (!ms || ms === 0) return '-'; if (ms >= 1000) return `${(ms/1000).toFixed(1)}s`; return `${Math.round(ms)}ms`; };
  const fmtDur = (ms: number) => {
    if (!ms || ms === 0) return '-';
    if (ms >= 60000) return `${(ms/60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms/1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  const lcpColor = (lcp: number) => {
    if (!lcp || lcp === 0) return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    if (lcp <= 2500) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (lcp <= 4000) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  };

  const lcpBarColor = (lcp: number) => {
    if (!lcp || lcp === 0) return 'bg-gray-300 dark:bg-gray-600';
    if (lcp <= 2500) return 'bg-green-500';
    if (lcp <= 4000) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('pages.title')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('pages.subtitle')}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <>
            {/* 4 Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('pages.totalPageViews')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(totalViews)}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('pages.uniquePages')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{uniquePagesCount}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('pages.pagesPerSession')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{avgPPS.toFixed(1)}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('pages.avgLoadTime')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {hasLcpData ? (
                    <span className={`px-2 py-1 rounded ${lcpColor(avgLcp)}`}>{fmtMs(avgLcp)}</span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">{t('pages.noMetrics')}</span>
                  )}
                </p>
              </div>
            </div>

            {/* Entry & Exit Pages */}
            {entryExit && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Entry Pages */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.entryPages')}</h2>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{t('pages.entryPagesDesc')}</p>
                  {entryExit.entryPages.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">{t('pages.noData')}</div>
                  ) : (
                    <div className="space-y-2">
                      {entryExit.entryPages.slice(0, 8).map((ep, i) => {
                        const maxEntries = entryExit.entryPages[0]?.entries || 1;
                        return (
                          <div key={ep.path} className="flex items-center space-x-3">
                            <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 shrink-0">{i+1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-mono text-gray-900 dark:text-white truncate">{ep.path}</span>
                                <span className="text-sm text-gray-500 ml-2 shrink-0">{ep.entries}</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${(ep.entries/maxEntries)*100}%` }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Exit Pages */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.exitPages')}</h2>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{t('pages.exitPagesDesc')}</p>
                  {entryExit.exitPages.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">{t('pages.noData')}</div>
                  ) : (
                    <div className="space-y-2">
                      {entryExit.exitPages.slice(0, 8).map((ep, i) => {
                        const maxExits = entryExit.exitPages[0]?.exits || 1;
                        return (
                          <div key={ep.path} className="flex items-center space-x-3">
                            <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 shrink-0">{i+1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-mono text-gray-900 dark:text-white truncate">{ep.path}</span>
                                <span className="text-sm text-gray-500 ml-2 shrink-0">{ep.exits}</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                <div className="bg-red-500 h-1.5 rounded-full transition-all" style={{ width: `${(ep.exits/maxExits)*100}%` }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scroll Depth Distribution */}
            {scrollDepth && scrollDepth.total > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.scrollDepth', 'Scroll Depth')}</h2>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.scrollDepthDesc', 'How far users scroll down your pages')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{scrollDepth.avgDepth.toFixed(0)}%</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.avgScrollDepth')}</p>
                  </div>
                </div>

                {/* Segmented bar */}
                <div className="relative mb-6">
                  <div className="flex rounded-xl overflow-hidden h-10 shadow-inner">
                    {SCROLL_SEGMENTS.map(seg => {
                      const val = scrollDepth.distribution[seg.key] || 0;
                      const pct = scrollDepth.total > 0 ? (val / scrollDepth.total) * 100 : 0;
                      if (pct < 1) return null;
                      return (
                        <div
                          key={seg.key}
                          className="relative flex items-center justify-center transition-all duration-300 group"
                          style={{ width: `${pct}%`, backgroundColor: seg.color }}
                        >
                          {pct >= 8 && (
                            <span className="text-white text-xs font-bold drop-shadow-sm">{pct.toFixed(0)}%</span>
                          )}
                          {/* Tooltip on hover */}
                          <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                            {seg.label}: {val.toLocaleString()} ({pct.toFixed(1)}%)
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Average depth marker */}
                  <div
                    className="absolute top-0 h-10 w-0.5 bg-white/80 dark:bg-gray-200/80 z-10"
                    style={{ left: `${scrollDepth.avgDepth}%` }}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap shadow">
                      avg {scrollDepth.avgDepth.toFixed(0)}%
                    </div>
                  </div>
                </div>

                {/* Segment legend with mini cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {SCROLL_SEGMENTS.map(seg => {
                    const val = scrollDepth.distribution[seg.key] || 0;
                    const pct = scrollDepth.total > 0 ? (val / scrollDepth.total) * 100 : 0;
                    return (
                      <div key={seg.key} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40">
                        <div className="w-3 h-8 rounded-full shrink-0" style={{ backgroundColor: seg.color }}></div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{pct.toFixed(1)}%</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{seg.label}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">{val.toLocaleString()} users</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Page Flow — Sankey-style Diagram */}
            {pageFlow.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.pageFlow')}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('pages.pageFlowDesc')}</p>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center space-x-1">
                      <span className="inline-block w-3 h-1.5 rounded-full bg-cyan-500"></span>
                      <span>Source</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                      <span>Destination</span>
                    </span>
                  </div>
                </div>
                <PageFlowDiagram flows={pageFlow} />
              </div>
            )}

            {/* Top Pages Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('pages.topPages')}</h2>
              {pages.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('pages.noData')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        <th className="pb-3 font-medium">{t('pages.path')}</th>
                        <th className="pb-3 font-medium text-right">{t('pages.views')}</th>
                        <th className="pb-3 font-medium text-right">{t('pages.uniqueSessions')}</th>
                        <th className="pb-3 font-medium text-right">{t('pages.avgDuration')}</th>
                        <th className="pb-3 font-medium text-right">{t('pages.avgScrollDepth')}</th>
                        {hasLcpData && <th className="pb-3 font-medium text-right">{t('pages.avgLcp')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map((page, idx) => {
                        const maxViews = pages[0]?.views || 1;
                        const viewPct = (page.views / maxViews) * 100;
                        return (
                          <tr key={page.path} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="py-3">
                              <div className="flex items-center space-x-3">
                                <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 shrink-0">{idx+1}</span>
                                <div className="min-w-0">
                                  <span className="font-mono text-sm text-gray-900 dark:text-white block truncate">{page.path}</span>
                                  <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-1 mt-1">
                                    <div className="bg-cyan-500 h-1 rounded-full" style={{ width: `${viewPct}%` }}></div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 text-right font-medium text-gray-900 dark:text-white">{fmt(page.views)}</td>
                            <td className="py-3 text-right text-gray-500">{fmt(page.uniqueSessions)}</td>
                            <td className="py-3 text-right text-gray-500">{fmtDur(page.avgDuration)}</td>
                            <td className="py-3 text-right text-gray-500">
                              {(() => {
                                const depth = scrollByPage.get(page.path) ?? page.avgScrollDepth;
                                if (!depth || depth === 0) return '-';
                                const barColor = depth >= 75 ? 'bg-green-500' : depth >= 50 ? 'bg-cyan-500' : depth >= 25 ? 'bg-yellow-500' : 'bg-red-500';
                                return (
                                  <div className="flex items-center justify-end space-x-2">
                                    <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                      <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${depth}%` }}></div>
                                    </div>
                                    <span className="text-xs font-medium">{depth.toFixed(0)}%</span>
                                  </div>
                                );
                              })()}
                            </td>
                            {hasLcpData && (
                              <td className="py-3 text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full ${lcpBarColor(page.avgLcp)}`} style={{ width: `${Math.min((page.avgLcp / 6000) * 100, 100)}%` }}></div>
                                  </div>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${lcpColor(page.avgLcp)}`}>
                                    {fmtMs(page.avgLcp)}
                                  </span>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* LCP Legend */}
            {hasLcpData && (
              <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
                <div className="flex items-center space-x-1.5"><div className="w-3 h-3 rounded bg-green-500"></div><span>{t('pages.good')} (&le;2.5s)</span></div>
                <div className="flex items-center space-x-1.5"><div className="w-3 h-3 rounded bg-yellow-500"></div><span>{t('pages.needsImprovement')} (&le;4s)</span></div>
                <div className="flex items-center space-x-1.5"><div className="w-3 h-3 rounded bg-red-500"></div><span>{t('pages.poor')} (&gt;4s)</span></div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
